import { Component, DestroyRef, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { API, PostDTO, TopicFullDTO, UserDTO } from '../../../services/api/api';
import { SbDateTime } from '../../../services/api/api-date-time';
import { DateTime } from 'luxon';
import { UtilService } from '../../../services/util.service';
import { DiscussionService } from '../../../services/discussion.service';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { take } from 'rxjs/operators';

@Component({
  selector: 'sb-comment-component',
  templateUrl: './sb-comment-component.component.html',
  styleUrls: ['./sb-comment-component.component.scss'],
  standalone: false,
})
export class SbCommentComponentComponent implements OnInit {
  @Input() post: PostDTO;
  @Input() level: number;
  @Input() currentUser: UserDTO;
  @Input() solutionDisplay: boolean;
  @Input() theme: string;
  @Input() isSelf: boolean;

  @Output() newUserComment = new EventEmitter<boolean>();

  replyClicked: boolean;
  editClicked: boolean;
  reply: string;
  isSolution: boolean;
  isSolutionClickable: boolean;
  topic: TopicFullDTO;

  constructor(
    public api: API,
    public util: UtilService,
    public discussionService: DiscussionService,
    private destroyRef: DestroyRef
  ) {
    this.theme = '';
    this.replyClicked = false;
    this.reply = '';
    this.level = 0;
    this.editClicked = false;
    this.isSolution = false;
    this.isSolutionClickable = true;
    this.solutionDisplay = false;
  }

  ngOnInit(): void {
    this.topic = this.discussionService.selectedTopic;
    if (this.topic.solution) {
      if (this.topic.solution.id === this.post.id) {
        this.isSolution = true;
      }
      this.isSolutionClickable = false;
    }
  }

  replyClick($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    this.replyClicked = !this.replyClicked;
  }

  sendReply($event: MouseEvent) {
    this.replyClick($event);
    this.discussionService.sendReply(this.reply, this.post.id, false).then();
    this.newUserComment.emit(true);
    this.reply = '';
  }

  checkLevel(depth: number, isSelf: boolean) {
    if (this.theme === 'dark-theme') {
      switch (depth) {
        case 0:
          return isSelf ? 'rgba(225,233,255,0.2)' : 'rgba(201,247,245,0.2)';
        case 1:
          return isSelf ? 'rgba(238,242,253,0.2)' : 'rgba(225,252,251,0.2)';
        case 2:
          return isSelf ? 'rgba(250,251,253,0.2)' : 'rgba(243,255,255,0.2)';
        case 3:
          return isSelf ? 'rgba(249,249,249,0.2)' : 'rgba(244,251,251,0.2)';
        case 4:
        default:
          return 'rgba(255,255,255,0.2)';
      }
    } else {
      switch (depth) {
        case 0:
          return isSelf ? '#E1E9FF' : '#C9F7F5';
        case 1:
          return isSelf ? '#eef2fd' : '#E1FCFB';
        case 2:
          return isSelf ? '#fafbfd' : '#F3FFFF';
        case 3:
          return isSelf ? '#f9f9f9' : '#F4FBFB';
        case 4:
        default:
          return '#FFFFFF';
      }
    }
  }

  passedTime(date: SbDateTime, until: string): string {
    return this.util.passedTime(date, until);
  }

  getCurrentTime(): SbDateTime {
    return new SbDateTime(DateTime.local());
  }

  editComment($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    if (!this.editClicked) {
      this.reply = this.post.text;
      this.editClicked = true;
    } else {
      this.post.text = this.reply;
      this.api
        .shared_Discussion_editPost(this.post)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(() => {});
      this.newUserComment.emit(true);
      this.reply = '';
      this.editClicked = false;
    }
  }

  deleteComment($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    if (this.editClicked) {
      this.editClicked = false;
      this.reply = '';
    } else {
      this.api
        .shared_Discussion_deletePost(this.post.id)
        .pipe(takeUntilDestroyed(this.destroyRef), take(1))
        .subscribe(() => {});
      this.newUserComment.emit(true);
    }
  }

  markSolution($event: MouseEvent) {
    if ($event) {
      $event.stopPropagation();
    }
    this.isSolution = !this.isSolution;
    this.discussionService.setSolution(this.topic.id, this.post.id, this.isSolution);
    this.newUserComment.emit(true);
  }
}
